import {
  getAllTransportSubcategoryKeywords,
  getContextKeywords,
  getLocaleAwareContextKeywords,
  getLocaleAwareSubcategoryKeywords,
  getTransportSubcategoryPriority,
} from './languages';
import {
  type SubcategoryConfig,
  type SubcategoryResult,
  detectSubcategory,
} from './subcategoryDetection';
import { EXPENSE_TAXONOMY } from './taxonomy';

export const TRANSPORT_KEYWORDS = getAllTransportSubcategoryKeywords();
export const FLIGHT_CONTEXT_KEYWORDS = getContextKeywords('flight');
export const FUEL_CONTEXT_KEYWORDS = getContextKeywords('fuel');
export const TRANSPORT_TICKET_KEYWORDS = getContextKeywords('transportTickets');

/** Derived from the canonical `EXPENSE_TAXONOMY`. See taxonomy.ts. */
export const VALID_TRANSPORT_SUBCATEGORIES =
  EXPENSE_TAXONOMY.transport as readonly string[] as string[];

export const TRANSPORT_CONTEXT_KEYWORDS = {
  flight: FLIGHT_CONTEXT_KEYWORDS,
  fuel: FUEL_CONTEXT_KEYWORDS,
};

export const TRANSPORT_CONFIG: SubcategoryConfig = {
  keywords: TRANSPORT_KEYWORDS,
  validSubcategories: VALID_TRANSPORT_SUBCATEGORIES,
  contextKeywords: TRANSPORT_CONTEXT_KEYWORDS,
  minConfidenceThreshold: 0.3,
  subcategoryPriority: getTransportSubcategoryPriority(),
};

export function detectTransportSubcategory(description: string): string {
  return detectSubcategory(description, TRANSPORT_CONFIG).subcategory;
}

export function detectTransportSubcategoryWithConfidence(
  description: string
): SubcategoryResult {
  return detectSubcategory(description, TRANSPORT_CONFIG);
}

/**
 * Locale-aware variant: combines global brand keywords with the user's
 * primary-language common-word keywords. Falls back to the legacy
 * all-language merge for any subcategory whose brand entry is still
 * empty in `shared/brands.ts` (incremental migration safety net).
 *
 * Returns the "default" sentinel (confidence 0) when the locale tag
 * doesn't resolve to a supported language — callers should let the
 * cloud LLM tier handle those users.
 */
export function detectTransportSubcategoryWithLocale(
  description: string,
  locale: string | undefined | null
): SubcategoryResult {
  const keywords = getLocaleAwareSubcategoryKeywords(
    b => b.transportSubcategories,
    k => k.transportSubcategories,
    locale
  );
  if (!keywords) {
    return { subcategory: 'default', confidence: 0, matchedKeywords: [] };
  }
  // Transport context vocabulary ('trip/road trip' EN, 'viaje/trayecto'
  // ES, 'voyage/trajet' FR, 'viagem/jornada' PT, 'tanken/sprit' DE) is
  // per-language. Use only the user's locale's context, not the merged
  // global union — otherwise an EN user typing French 'voyage' would
  // boost the fuel subcategory via FR context.
  const flightCtx = getLocaleAwareContextKeywords('flight', locale) ?? [];
  const fuelCtx = getLocaleAwareContextKeywords('fuel', locale) ?? [];
  return detectSubcategory(description, {
    ...TRANSPORT_CONFIG,
    keywords,
    contextKeywords: { flight: flightCtx, fuel: fuelCtx },
  });
}
